import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FolderOpen, Copy, Check, WifiSlash } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { nasApi } from './nasApi';
import { NAS_I18N } from './nasI18n';
import { getLanBase } from '@/lib/lanStorage';

/** Copy helper that also works on non-secure origins. */
async function copyText(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

export function useLanActive() {
  const [lan, setLan] = useState(() => !!getLanBase());
  useEffect(() => {
    const onChange = () => setLan(!!getLanBase());
    window.addEventListener('lan-base-changed', onChange);
    const t = setInterval(onChange, 5000);
    return () => { window.removeEventListener('lan-base-changed', onChange); clearInterval(t); };
  }, []);
  return lan;
}

export function CopyPathButton({ value, label = 'パスをコピー', testid }) {
  const [done, setDone] = useState(false);
  if (!value) return null;
  return (
    <button type="button" title={label} aria-label={label} data-testid={testid}
      onClick={async (e) => {
        e.stopPropagation();
        const ok = await copyText(value);
        if (!ok) { toast.error('コピーできませんでした'); return; }
        setDone(true);
        setTimeout(() => setDone(false), 1500);
        toast.success('パスをコピーしました');
      }}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700">
      {done ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
    </button>
  );
}

/**
 * 元の場所を開く — Windowsのエクスプローラーに貼り付けられるパスを取得して
 * クリップボードにコピーする。ブラウザはエクスプローラーを直接起動できないため、
 * 「コピー → Win+R に貼り付け」の一手で目的のフォルダーに到達できるようにする。
 * LAN未接続時は自動で無効化する。
 */
export default function NasOpenLocation({ nodeId, compact = false, className = '' }) {
  const lan = useLanActive();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (e) => {
    if (e) e.stopPropagation();
    if (!lan) { toast.error(NAS_I18N.location_lan_only); return; }
    setBusy(true);
    try {
      const d = await nasApi.fileLocation(nodeId, true);
      setResult(d);
      const target = d.folder_windows_path || d.windows_path || d.unc_path;
      const ok = await copyText(target);
      toast.success(ok ? `フォルダーのパスをコピーしました — Win+R に貼り付けてEnter：${target}`
        : `フォルダーのパス：${target}`, { duration: 9000 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || '元の場所を取得できませんでした');
    } finally { setBusy(false); }
  };

  if (compact) {
    return (
      <button type="button" onClick={run} disabled={busy || !lan}
        title={lan ? NAS_I18N.location_open : NAS_I18N.location_lan_only}
        aria-label={NAS_I18N.location_open}
        data-testid={`nas-open-location-${nodeId}`}
        className={`inline-flex h-7 w-7 items-center justify-center rounded-md ${lan
          ? 'text-teal-700 hover:bg-teal-50' : 'text-zinc-300 cursor-not-allowed'} ${className}`}>
        {lan ? <FolderOpen size={15} weight="duotone" /> : <WifiSlash size={14} />}
      </button>
    );
  }

  return (
    <div className={className} data-testid="nas-open-location-block">
      <Button onClick={run} disabled={busy || !lan}
        className="h-9 bg-teal-700 hover:bg-teal-800 text-white disabled:bg-zinc-200 disabled:text-zinc-400"
        data-testid="nas-open-location-btn">
        <FolderOpen size={15} weight="duotone" className="mr-1.5" />
        {NAS_I18N.location_open}
      </Button>
      {!lan && (
        <p className="mt-1.5 text-xs text-amber-700 inline-flex items-center gap-1"
          data-testid="nas-open-location-lan-notice">
          <WifiSlash size={12} /> {NAS_I18N.location_lan_only}
        </p>
      )}
      {result && (
        <div className="mt-2 rounded-md border border-zinc-200 bg-zinc-50 p-2"
          data-testid="nas-open-location-result">
          <p className="text-[11px] text-zinc-500">{NAS_I18N.location_paste_hint}</p>
          {[[result.folder_windows_path || result.windows_path, 'フォルダー'],
            [result.windows_path, 'ファイル'],
            [result.unc_path, 'UNC']].filter(([v]) => v).map(([v, label]) => (
            <div key={label} className="mt-1 flex items-center gap-1.5">
              <span className="w-[68px] shrink-0 text-[10.5px] text-zinc-400">{label}</span>
              <code className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-zinc-800"
                title={v}>{v}</code>
              <CopyPathButton value={v} testid={`nas-copy-${label}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
